import React from 'react';
import { LabSample } from '../../contexts/LabSampleContext';

interface LabSampleTestPlanningProps {
    sample: LabSample;
    editedSample: LabSample;
    isEditing: boolean;
    setEditedSample: (sample: LabSample) => void;
}

export const LabSampleTestPlanning: React.FC<LabSampleTestPlanningProps> = ({
    sample,
    editedSample,
    isEditing,
    setEditedSample,
}) => {
    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Test Planning</h2>
            {isEditing ? (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                        <select
                            value={editedSample.status}
                            onChange={e => setEditedSample({ ...editedSample, status: e.target.value as LabSample['status'] })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="received">Received</option>
                            <option value="queued">Queued</option>
                            <option value="testing">Testing</option>
                            <option value="analysis">Analysis</option>
                            <option value="complete">Complete</option>
                            <option value="on_hold">On Hold</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Lab</label>
                        <input
                            type="text"
                            value={editedSample.assignedLab || ''}
                            onChange={e => setEditedSample({ ...editedSample, assignedLab: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Technician</label>
                        <input
                            type="text"
                            value={editedSample.assignedTechnician || ''}
                            onChange={e => setEditedSample({ ...editedSample, assignedTechnician: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Test Due Date</label>
                        <input
                            type="date"
                            value={editedSample.testDueDate || ''}
                            onChange={e => setEditedSample({ ...editedSample, testDueDate: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Lab</label>
                        <p className="text-gray-900">{sample.assignedLab || 'Not assigned'}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Technician</label>
                        <p className="text-gray-900">{sample.assignedTechnician || 'Not assigned'}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Schedule Date</label>
                        <p className="text-gray-900">
                            {sample.testScheduleDate ? new Date(sample.testScheduleDate).toLocaleDateString() : 'Not scheduled'}
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                        <p className="text-gray-900">
                            {sample.testDueDate ? new Date(sample.testDueDate).toLocaleDateString() : 'Not set'}
                        </p>
                    </div>
                </div>
            )}

            <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Requested Tests</label>
                <div className="flex flex-wrap gap-2">
                    {sample.requestedTests.map((test, idx) => (
                        <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                            {test}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
};
